import React from "react";
import { Stamp, MapPin, Calendar, AlertTriangle } from "lucide-react";
import { fmtMoney, DEFAULT_CURRENCY } from "../lib/money";
import { useTranslation } from "react-i18next";

export default function PermitEnrichmentPanel({ enrichment }) {
  const { t } = useTranslation("components");
  const e = enrichment || {};
  const currency = (e.currency || DEFAULT_CURRENCY).toUpperCase();
  const money = (n) => fmtMoney(n, currency) ?? "—";
  return (
    <section className="bg-white border border-zinc-200 rounded-xl p-6" data-testid="permit-enrichment-panel">
      <div className="flex items-center gap-2 mb-5">
        <div className="w-7 h-7 rounded-md grid place-items-center bg-amber-50 text-amber-700">
          <Stamp size={14} strokeWidth={1.75} />
        </div>
        <h2 className="font-display font-semibold text-base tracking-tight">{t("enrichment.permit.section_title")}</h2>
      </div>

      <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-4 mb-6">
        <Tile
          label={t("enrichment.permit.subtype")}
          value={e.permit_subtype ? t(`enrichment.permit.permit_subtype_options.${e.permit_subtype}`, e.permit_subtype.replace(/_/g, " ")) : "—"}
          testid="permit-enrich-subtype"
        />
        <Tile label={t("enrichment.permit.permit_number")} value={e.permit_number || "—"} testid="permit-enrich-number" />
        <Tile label={t("enrichment.permit.issuing_agency")} value={e.issuing_agency || "—"} testid="permit-enrich-agency" />
        <Tile label={t("enrichment.permit.jurisdiction")} value={e.jurisdiction || "—"} icon={MapPin} testid="permit-enrich-jurisdiction" />
      </div>

      {e.permit_class && (
        <div className="mb-5">
          <p className="text-[11px] uppercase tracking-[0.18em] text-zinc-500 mb-1">{t("enrichment.permit.class_label")}</p>
          <p className="font-display font-semibold text-zinc-950" data-testid="permit-enrich-class">{e.permit_class}</p>
        </div>
      )}

      {e.address_covered && (
        <div className="mb-5" data-testid="permit-enrich-address">
          <p className="text-[11px] uppercase tracking-[0.18em] text-zinc-500 mb-1 flex items-center gap-1">
            <MapPin size={10} />{t("enrichment.permit.address_covered")}
          </p>
          <p className="font-mono text-xs bg-zinc-100 px-2 py-1 rounded inline-block">{e.address_covered}</p>
        </div>
      )}

      {e.covered_activities?.length > 0 && (
        <div className="mb-5" data-testid="permit-enrich-activities">
          <p className="text-[11px] uppercase tracking-[0.18em] text-zinc-500 mb-2 font-medium">
            {t("enrichment.permit.activities_label", { count: e.covered_activities.length })}
          </p>
          <ul className="space-y-1">
            {e.covered_activities.map((a, i) => (
              <li key={i} className="text-sm text-zinc-800 leading-snug flex gap-1.5" data-testid={`permit-activity-${i}`}>
                <span>•</span><span>{a}</span>
              </li>
            ))}
          </ul>
        </div>
      )}

      {e.conditions?.length > 0 && (
        <div className="mb-5" data-testid="permit-enrich-conditions">
          <p className="text-[11px] uppercase tracking-[0.18em] text-zinc-500 mb-2 font-medium">
            {t("enrichment.permit.operating_conditions", { count: e.conditions.length })}
          </p>
          <ul className="space-y-1">
            {e.conditions.map((c, i) => (
              <li key={i} className="text-sm text-zinc-800 leading-snug flex gap-1.5 px-3 py-1.5 rounded bg-amber-50/40 border border-amber-100" data-testid={`permit-condition-${i}`}>
                <AlertTriangle size={12} className="text-amber-700 shrink-0 mt-0.5" />
                <span>{c}</span>
              </li>
            ))}
          </ul>
        </div>
      )}

      <div className="flex flex-wrap gap-1.5 mb-3">
        {e.inspection_required && <Flag tone="amber">{t("enrichment.permit.inspection_required")}</Flag>}
        {e.post_at_premises && <Flag tone="zinc">{t("enrichment.permit.post_at_premises")}</Flag>}
        {e.transferable === false && <Flag tone="rose">{t("enrichment.permit.non_transferable")}</Flag>}
        {e.transferable === true && <Flag tone="emerald">{t("enrichment.permit.transferable")}</Flag>}
        {e.renewal_lead_time_days != null && (
          <Flag tone="zinc"><Calendar size={9} className="inline -mt-0.5 mr-0.5" />{t("enrichment.permit.renew_ahead", { count: e.renewal_lead_time_days })}</Flag>
        )}
      </div>

      {e.fees?.length > 0 && (
        <div className="mb-3" data-testid="permit-enrich-fees">
          <p className="text-[11px] uppercase tracking-[0.18em] text-zinc-500 mb-2 font-medium">{t("enrichment.permit.fees_label")}</p>
          <div className="grid sm:grid-cols-2 gap-2">
            {e.fees.map((f, i) => (
              <div key={i} className="flex items-center justify-between px-3 py-2 rounded-lg bg-zinc-50 border border-zinc-200 text-sm" data-testid={`permit-fee-${i}`}>
                <span className="font-medium">{f.name}</span>
                <span className="font-mono text-xs tabular-nums text-zinc-700">
                  {money(f.amount)}
                  <span className="text-[10px] uppercase tracking-wider text-zinc-500 ml-1.5">{t(`enrichment.permit.fee_timing_options.${f.timing}`, (f.timing || "").replace(/_/g, " "))}</span>
                </span>
              </div>
            ))}
          </div>
        </div>
      )}

      {e.summary && <p className="text-sm text-zinc-700 mt-3" data-testid="permit-enrich-summary">{e.summary}</p>}
    </section>
  );
}

function Tile({ label, value, icon: Icon, testid }) {
  return (
    <div data-testid={testid}>
      <p className="text-[11px] uppercase tracking-[0.18em] text-zinc-500 mb-1 flex items-center gap-1">
        {Icon && <Icon size={10} />}{label}
      </p>
      <p className="font-display font-semibold text-zinc-950">{value}</p>
    </div>
  );
}

function Flag({ tone, children }) {
  const tones = {
    rose: "bg-rose-50 text-rose-700 border-rose-200",
    amber: "bg-amber-50 text-amber-800 border-amber-200",
    emerald: "bg-emerald-50 text-emerald-700 border-emerald-200",
    zinc: "bg-zinc-100 text-zinc-700 border-zinc-200",
  };
  return (
    <span className={`text-[10px] uppercase tracking-wider px-1.5 py-0.5 rounded border font-semibold ${tones[tone]}`}>
      {children}
    </span>
  );
}
